import { SessionContext } from "@/context/SessionContext";
import ConductionAPI from "@/javascript/conduction-api.ts";
import { User } from "@/javascript/user-objects.ts";
import { Utils } from "@/javascript/utils";
import { Box, Button, Flex, Group, Modal, PasswordInput, Stack, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck, IconLock } from "@tabler/icons-react";
import { useCallback, useContext } from "react";

interface StudentPasswordEditorModalProps {
    showModal: boolean;
    setShowModal: (show: boolean) => void;
	user: User | null;
}

const StudentPasswordEditorModal: React.FC<StudentPasswordEditorModalProps> = ({ user, showModal, setShowModal }) => {
	const session = useContext(SessionContext);
	if (session === undefined) {
		throw new Error("This modal must be a child of SessionContext.");
	}

	const form = useForm({
		initialValues: {
			newPassword: "",
		},
		validate: {
			newPassword: (value) => {
				if (value.length < 6) return "Password must be at least 6 characters long.";
				if (value.length > 30) return "Password must not exceed 30 characters.";
				return null;
			},
		},
	});

	const handleSubmit = useCallback(async ({ newPassword }: typeof form.values) => {
		if (session.token === null || user === null) {
			return;
		}

		const notificationId = Utils.displayNotification(
			"Updating password....",
			null,
			"blue",
			"loading"
		);

		try {
			await ConductionAPI.updatePassword(user, newPassword, session.token);
			Utils.updateNotification(
				notificationId,
				`Password successfully updated for user: ${user.username}!`,
				undefined,
				"green",
				<IconCheck size={16} />
			);
			form.reset();
			setShowModal(false);
		} catch (error) {
			console.error("Error updating user information", error);
			Utils.updateNotification(
				notificationId,
				"Update Failed",
				"An error occurred while updating the password.",
				"red",
				<IconAlertCircle size={16} />,
				true,
				false
			);
		}
	}, [user, session.token]);

	if (user === null) return null;

	return (
		<Modal
			opened={showModal}
			onClose={() => setShowModal(false)}
			title="User Settings"
			centered
			size="md"
			styles={{ title: { fontSize: 26, fontVariant: "all-small-caps" } }}
			withCloseButton
		>
			<Flex
				align="stretch"
				justify="space-between"
				direction={"column"}
			>
				<Text size="lg">
					Change password for user: {user.username}
				</Text>
				<Box>
					<Stack>
						<form onSubmit={form.onSubmit(handleSubmit)}>
							<Stack>
								<PasswordInput
									label={
										<Group>
											<IconLock size={20} />
											<Text>New Password</Text>
										</Group>
									}
									{...form.getInputProps("newPassword")}
									required
									withAsterisk={false}
								/>

								<Button
									type="submit"
									radius="xl"
									size='sm'
									loading={form.submitting}
								>
									Update Password
								</Button>
							</Stack>
						</form>
					</Stack>
				</Box>
			</Flex>
		</Modal>
	);
};

export default StudentPasswordEditorModal;
